import { ChipAutocomplete } from '@/dsm-components/chip';
import { useLazyGetUtilitiesQuery } from '@/services/utilities';
import { useCallback, useMemo, useRef } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { DialogFormValues } from './checklistItemInputOptions';
import { serializeParamsRaw } from './paramsRaw';

const debounceMs = 300;

interface UtilityParamAdderProps {
  fieldPrefix: `conditions` | `inputs.${number}.conditions`;
  currentParams: string[];
  conditionIndex: number;
  setValue: UseFormSetValue<DialogFormValues>;
  onLabelAdd: (value: string, label: string) => void;
}

export const UtilityParamAdder = ({
  fieldPrefix,
  currentParams,
  conditionIndex,
  setValue,
  onLabelAdd,
}: UtilityParamAdderProps) => {
  const [trigger, { data, isFetching }] = useLazyGetUtilitiesQuery();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const items = useMemo(() => {
    if (!data) return [];
    return data
      .filter((utility: { lseId: number; name: string }) => !currentParams.includes(utility.lseId.toString()))
      .map((utility: { lseId: number; name: string }) => ({
        value: utility.lseId.toString(),
        label: utility.name,
      }));
  }, [data, currentParams]);

  const handleSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        trigger({ searchTerm: term });
      }, debounceMs);
    },
    [trigger],
  );

  const handleSelect = useCallback(
    (value: string, label: string) => {
      onLabelAdd(value, label);
      if (!currentParams.includes(value)) {
        const newParams = [...currentParams, value];
        setValue(`${fieldPrefix}.${conditionIndex}.paramsRaw`, serializeParamsRaw(newParams));
      }
    },
    [fieldPrefix, currentParams, conditionIndex, setValue, onLabelAdd],
  );

  return (
    <ChipAutocomplete
      items={items}
      onSelect={handleSelect}
      onSearch={handleSearch}
      isLoading={isFetching}
      placeholder="+ utility"
    />
  );
};
